import { normalizeName } from './matching/normalize.js';
import { parseAmountToMinor } from './money.js';
/**
 * Telling a reviewer's correction apart from what extraction read.
 *
 * `extraction.fields[x].value` keeps the machine reading; a correction is
 * saved on the invoice itself. A field counts as edited while the two
 * disagree, compared here in one form so that "35,400.00" read off the page
 * and 3540000 paise saved are the same figure. The server marks fields with
 * this, and the review screen uses it to show an edit as it is typed.
 */
export const EDITABLE_FIELDS = [
    'vendorName',
    'invoiceNumber',
    'invoiceDate',
    'dueDate',
    'subtotal',
    'taxAmount',
    'totalAmount',
    'gstin',
    'pan',
];
const KIND = {
    vendorName: 'name',
    invoiceNumber: 'reference',
    invoiceDate: 'date',
    dueDate: 'date',
    subtotal: 'amount',
    taxAmount: 'amount',
    totalAmount: 'amount',
    gstin: 'reference',
    pan: 'reference',
};
/**
 * A field's value in one comparable form, or `undefined` for "no value".
 * `extracted` values are text or major units as read off the page;
 * `saved` values are the invoice's own: minor units, dates, `yyyy-mm-dd`.
 */
export function comparableFieldValue(field, value, from) {
    if (value === null || value === undefined || value === '')
        return undefined;
    switch (KIND[field]) {
        case 'name':
            return normalizeName(String(value)) || undefined;
        case 'reference':
            return String(value).trim().toUpperCase() || undefined;
        case 'date':
            if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
            }
            // A saved date may arrive as a full ISO timestamp.
            return toIsoDate(from === 'saved' ? String(value).slice(0, 10) : String(value));
        case 'amount': {
            if (from === 'extracted')
                return parseAmountToMinor(value) ?? undefined;
            const minor = Number(value);
            return Number.isFinite(minor) ? minor : undefined;
        }
    }
}
/** Whether a saved value differs from what extraction read for the field. */
export function differsFromExtracted(field, extracted, saved) {
    return (comparableFieldValue(field, saved, 'saved') !==
        comparableFieldValue(field, extracted, 'extracted'));
}
// ── Dates ──────────────────────────────────────────────────
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
/** Any supported layout to `yyyy-mm-dd`; numeric dates are day-first, as Indian invoices are. */
export function toIsoDate(raw) {
    const value = raw.trim().replace(/(\d)(st|nd|rd|th)\b/i, '$1');
    let year;
    let month;
    let day;
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
    const numeric = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/.exec(value);
    const dayFirst = /^(\d{1,2})[\s./-]*([A-Za-z]{3,9})\.?[\s,./-]*(\d{2,4})$/.exec(value);
    const monthFirst = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{2,4})$/.exec(value);
    if (iso)
        [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    else if (numeric)
        [day, month, year] = [Number(numeric[1]), Number(numeric[2]), Number(numeric[3])];
    else if (dayFirst)
        [day, month, year] = [Number(dayFirst[1]), monthOf(dayFirst[2]), Number(dayFirst[3])];
    else if (monthFirst)
        [month, day, year] = [monthOf(monthFirst[1]), Number(monthFirst[2]), Number(monthFirst[3])];
    if (!year || !month || !day)
        return undefined;
    if (year < 100)
        year += 2000;
    const date = new Date(Date.UTC(year, month - 1, day));
    // Rejects 31/02 and the like instead of rolling into the next month.
    if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day)
        return undefined;
    return date.toISOString().slice(0, 10);
}
function monthOf(name) {
    const index = MONTHS.indexOf(name.slice(0, 3).toLowerCase());
    return index >= 0 ? index + 1 : undefined;
}
//# sourceMappingURL=fieldEdits.js.map